/**
 * ProductTourBanner — slim "Take a 5-min product tour" CTA for new users.
 *
 * Behaviour:
 *   • Only renders on the dashboards (/dashboard, /business-dashboard).
 *   • Hides itself once the user has completed ≥ 3 tours.
 *   • Hides for 7 days after "Maybe later".
 *   • Launches the most-relevant tour for the route the user is on by
 *     writing `pendingTour` into localStorage — TourLauncher picks it up.
 *
 * Stays naggy-free: dismissed state survives reloads + the
 * completed-count check means power-users never see it again.
 */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Gift, ArrowRight, X } from 'lucide-react';
import { tours } from '../lib/tourDefinitions';

const SHOW_ON_ROUTES = ['/dashboard', '/business-dashboard'];
const ROUTE_TO_TOUR = {
  '/dashboard':          'dashboard',
  '/business-dashboard': 'business_dashboard',
};
const COMPLETED_KEY = 'completedTours';
const DISMISSED_KEY = 'product_tour_banner_dismissed_at';
const SUPPRESS_MS = 7 * 24 * 60 * 60 * 1000;   // 7 days
const COMPLETED_FLOOR = 3;                      // hide once ≥3 tours done

export default function ProductTourBanner() {
  const location = useLocation();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!SHOW_ON_ROUTES.includes(location.pathname)) {
      setVisible(false);
      return;
    }
    try {
      const dismissed = parseInt(localStorage.getItem(DISMISSED_KEY) || '0', 10);
      if (dismissed && Date.now() - dismissed < SUPPRESS_MS) {
        setVisible(false);
        return;
      }
      const completed = JSON.parse(localStorage.getItem(COMPLETED_KEY) || '[]');
      const realCount = completed.filter((id) => tours[id]).length;
      setVisible(realCount < COMPLETED_FLOOR);
    } catch {
      setVisible(true);
    }
  }, [location.pathname]);

  const launch = () => {
    const tourId = ROUTE_TO_TOUR[location.pathname] || 'business_dashboard';
    try { localStorage.setItem('pendingTour', tourId); } catch { /* ignore */ }
    // We're already on the right page in most cases, but navigate-replace
    // forces TourLauncher's useEffect to re-fire so the popover opens.
    navigate(location.pathname, { replace: true });
    setVisible(false);
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISSED_KEY, String(Date.now())); } catch { /* ignore */ }
    setVisible(false);
  };

  const browse = () => navigate('/guided-tours');

  if (!visible) return null;

  return (
    <div
      className="flex items-center gap-3 rounded-xl border border-emerald-500/30 bg-gradient-to-r from-emerald-500/10 via-emerald-500/5 to-transparent px-4 py-3 mb-4"
      data-testid="product-tour-banner"
    >
      <div className="w-9 h-9 rounded-lg bg-emerald-500/15 flex items-center justify-center shrink-0">
        <Gift className="w-4.5 h-4.5 text-emerald-600 dark:text-emerald-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold leading-tight">
          New here? Take a 5-min product tour.
        </p>
        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
          See how dashboards, AI, and your books fit together — built for the page you're on.
        </p>
      </div>
      <Button
        size="sm"
        onClick={launch}
        className="bg-emerald-600 hover:bg-emerald-500 text-white gap-1.5 hidden sm:inline-flex"
        data-testid="product-tour-banner-cta"
      >
        Start tour
        <ArrowRight className="w-3.5 h-3.5" />
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={browse}
        className="text-xs hidden md:inline-flex"
        data-testid="product-tour-banner-browse"
      >
        Browse all tours
      </Button>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="text-muted-foreground hover:text-foreground shrink-0"
        data-testid="product-tour-banner-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
